import { computed, watch } from 'vue'
import { useAppConfigStore } from '@/stores/appConfigStore'
import { useVerticalMenuStore } from '@/stores/verticalMenuStore'

export default function usAppConfig(pinia = undefined) {
  const appConfigStore = useAppConfigStore(pinia)
  const verticalMenuStore = useVerticalMenuStore(pinia)

  // ------------------------------------------------
  // isVerticalMenuCollapsed
  // ------------------------------------------------
  const isVerticalMenuCollapsed = computed({
    get: () => verticalMenuStore.isVerticalMenuCollapsed,
    set: val => {
      verticalMenuStore.updateVerticalMenuCollapsed(val)
    },
  })

  // ------------------------------------------------
  // RTL
  // ------------------------------------------------
  const isRTL = computed({
    get: () => appConfigStore.layout.isRTL,
    set: val => {
      if (appConfigStore.layout.isRTL !== val) {
        appConfigStore.toggleRtl()
      }
    },
  })

  // ------------------------------------------------
  // Skin
  // ------------------------------------------------
  const skin = computed({
    get: () => appConfigStore.layout.skin,
    set: val => {
      appConfigStore.updateSkin(val)
    },
  })

  const skinClasses = computed(() => {
    if (skin.value === 'bordered') return 'bordered-layout'
    if (skin.value === 'semi-dark') return 'semi-dark-layout'

    // Do not return any class for dark layout because dark layout updates class in body
    // Do not return any class for light layout as that is default layout
    return null
  })


  // ------------------------------------------------
  // routerTransition
  // ------------------------------------------------
  const routerTransition = computed({
    get: () => appConfigStore.layout.routerTransition,
    set: val => {
      appConfigStore.updateRouterTransition(val)
    },
  })

  // *===============================================---*
  // *--------- LAYOUT ---------------------------------------*
  // *===============================================---*

  // ------------------------------------------------
  // layoutType
  // ------------------------------------------------

  const layoutType = computed({
    get: () => appConfigStore.layout.type,
    set: val => {
      appConfigStore.updateLayoutType(val)
    },
  })

  // Reset skin if skin is semi-dark and move to horizontal layout
  watch(layoutType, val => {
    if (val === 'horizontal' && skin.value === 'semi-dark') skin.value = 'light'
  })

  // ------------------------------------------------
  // Content Width (Full/Boxed)
  // ------------------------------------------------
  const contentWidth = computed({
    get: () => appConfigStore.layout.contentWidth,
    set: val => {
      appConfigStore.updateContentWidth(val)
    },
  })

  // ------------------------------------------------
  // isNavMenuHidden
  // ------------------------------------------------
  const isNavMenuHidden = computed({
    get: () => appConfigStore.layout.menu.hidden,
    set: val => {
      appConfigStore.updateNavMenuHidden(val)
    },
  })

  // *===============================================---*
  // *--------- NAVBAR ---------------------------------------*
  // *===============================================---*

  const navbarBackgroundColor = computed({
    get: () => appConfigStore.layout.navbar.backgroundColor,
    set: val => {
      appConfigStore.updateNavbarConfig({ backgroundColor: val })
    },
  })

  const navbarType = computed({
    get: () => appConfigStore.layout.navbar.type,
    set: val => {
      appConfigStore.updateNavbarConfig({ type: val })
    },
  })

  // *===============================================---*
  // *--------- FOOTER ---------------------------------------*
  // *===============================================---*

  const footerType = computed({
    get: () => appConfigStore.layout.footer.type,
    set: val => {
      appConfigStore.updateFooterConfig({ type: val })
    },
  })

  return {
    isVerticalMenuCollapsed,
    isRTL,
    skin,
    skinClasses,
    routerTransition,

    // Navbar
    navbarBackgroundColor,
    navbarType,

    // Footer
    footerType,

    // Layout
    layoutType,
    contentWidth,
    isNavMenuHidden,
  }
}
